import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, DayPickerInput, Icon, InfiniteScrollingObserver, TextInput } from '~/components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import {
  EmptyState,
  EntityDetail,
  EntityIcon,
  EntityImage,
  EntityInfo,
  EntityName,
  EntityRow,
  Filter,
  Filters,
  Mono,
  Table,
  Td,
  Th,
} from './shared';
import EntitySearchFilter from './EntitySearchFilter';
import MemberDisplay from './MemberDisplay';
import WorkspaceDisplay from './WorkspaceDisplay';

const EntityFilter = styled(Filter)`
  width: 16rem;
`;

const DateFilter = styled(Filter)`
  width: 10rem;
`;

const SearchFilter = styled(Filter)`
  flex: 1;
  position: relative;

  > div {
    width: 100%;

    input {
      width: 100%;
      padding-right: 2rem;
    }
  }
`;

const BuildingIcon = styled(Icon).attrs({ icon: 'building' })`
  font-size: 1rem;
  color: ${colors.grey25};
`;

const BuildingIconSmall = styled(Icon).attrs({ icon: 'building' })`
  font-size: 0.875rem;
  color: ${colors.grey25};
`;

const SelectedLabel = styled.span`
  margin-left: 0.375rem;
`;

const WorkspaceTag = styled(EntityDetail).attrs({ as: 'span' })`
  font-weight: ${weights.normal};
`;

const ClearSearch = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  border-radius: 0;
  padding: 0;
  height: auto;
  color: ${colors.primary};
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    color: ${colors.accent};
    background: none;
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover td {
    background: ${colors.grey5};
  }
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 30rem;
  font-weight: ${weights.medium};
`;

const PreviewText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeletedBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  color: ${colors.danger};
  background: ${colors.danger}15;
  padding: 0.125rem 0.375rem;
  border-radius: 0.125rem;
`;

function renderWorkspaceItem(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.5rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIcon />}
      </EntityIcon>
      <EntityInfo>
        <EntityName>{item.label}</EntityName>
        {item.key && <EntityDetail>{item.key}</EntityDetail>}
      </EntityInfo>
    </EntityRow>
  );
}

function renderWorkspaceSelected(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.125rem">
        {item.imageUrl ? <EntityImage src={item.imageUrl} alt={item.label} /> : <BuildingIconSmall />}
      </EntityIcon>
      <SelectedLabel>{item.label}</SelectedLabel>
    </EntityRow>
  );
}

function renderMemberItem(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.5rem">
        <Avatar value={{ name: item.label, imageUrl: item.imageUrl }} isCircle hasBackground size={24} />
      </EntityIcon>
      <EntityInfo>
        <EntityName>
          {item.label}
          {item.workspaceName && <WorkspaceTag> · {item.workspaceName}</WorkspaceTag>}
        </EntityName>
        <EntityDetail>{item.email}</EntityDetail>
      </EntityInfo>
    </EntityRow>
  );
}

function renderMemberSelected(item) {
  return (
    <EntityRow>
      <EntityIcon $size="1.125rem">
        <Avatar value={{ name: item.label, imageUrl: item.imageUrl }} isCircle hasBackground size={18} />
      </EntityIcon>
      <SelectedLabel>{item.label}</SelectedLabel>
    </EntityRow>
  );
}

export default function ConversationList() {
  const api = useApi();
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const [start, setStart] = useState(urlParams.get('start') || undefined);
  const [end, setEnd] = useState(urlParams.get('end') || undefined);
  const [searchText, setSearchText] = useState(urlParams.get('q') || '');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Resolve workspace/member from URL params on mount
  const initialWorkspaceId = useRef(urlParams.get('workspaceId'));
  const initialMemberId = useRef(urlParams.get('memberId'));

  useEffect(() => {
    const wId = initialWorkspaceId.current;
    const mId = initialMemberId.current;
    if (!wId && !mId) return;

    const params = {};
    if (wId) params.workspaceId = wId;
    if (mId) params.memberId = mId;

    api.admin.aiReplay.resolve(params).then(({ data }) => {
      if (data.workspace) {
        setSelectedWorkspace({
          id: data.workspace.id,
          label: data.workspace.name,
          key: data.workspace.key,
          imageUrl: data.workspace.imageUrl,
        });
      }
      if (data.member) {
        setSelectedMember({
          id: data.member.id,
          label: data.member.name,
          email: data.member.email,
          imageUrl: data.member.imageUrl,
        });
      }
    });
  }, [api]);

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedWorkspace) params.set('workspaceId', selectedWorkspace.id);
    if (selectedMember) params.set('memberId', selectedMember.id);
    if (searchText) params.set('q', searchText);
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    history.replace({ search: qs ? `?${qs}` : '' });
  }, [selectedWorkspace, selectedMember, searchText, start, end, history]);

  const searchDebounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(urlParams.get('q') || '');

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchText]);

  const loadMore = useCallback(async () => {
    const params = { from: conversations.length };
    if (start) params.start = start;
    if (end) params.end = end;
    if (debouncedSearch) params.q = debouncedSearch;
    if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
    if (selectedMember) params.memberId = selectedMember.id;

    const { data } = await api.admin.aiReplay.list(params);
    setConversations((prev) => [...prev, ...data.conversations]);
    setHasMore(data.hasMore);
  }, [api, start, end, debouncedSearch, selectedWorkspace, selectedMember, conversations.length]);

  useEffect(() => {
    let cancelled = false;
    setConversations([]);
    setHasMore(false);
    setIsLoading(true);

    const fetch = async () => {
      try {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        if (debouncedSearch) params.q = debouncedSearch;
        if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
        if (selectedMember) params.memberId = selectedMember.id;

        const { data } = await api.admin.aiReplay.list(params);
        if (!cancelled) {
          setConversations(data.conversations);
          setHasMore(data.hasMore);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, debouncedSearch, selectedWorkspace, selectedMember]);

  const searchWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (text) params.q = text;
      if (from) params.from = from;
      const { data } = await api.admin.aiReplay.searchWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  const openWorkspaces = useCallback(
    async (text, from) => {
      const params = {};
      if (from) params.from = from;
      const { data } = await api.admin.aiReplay.recentWorkspaces(params);
      return {
        items: data.results.map((w) => ({ id: w.id, label: w.name, key: w.key, imageUrl: w.imageUrl })),
        hasMore: data.hasMore,
      };
    },
    [api],
  );

  const mapMembers = useCallback(
    (members) =>
      members.map((m) => ({
        id: m.id,
        label: m.name,
        email: m.email,
        imageUrl: m.imageUrl,
        workspaceId: m.workspace?.id,
        workspaceName: m.workspace?.name,
        workspaceKey: m.workspace?.key,
        workspaceImageUrl: m.workspace?.imageUrl,
      })),
    [],
  );

  const searchMembers = useCallback(
    async (text, from) => {
      const params = {};
      if (text) params.q = text;
      if (from) params.from = from;
      if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
      const { data } = await api.admin.aiReplay.searchMembers(params);
      return { items: mapMembers(data.results), hasMore: data.hasMore };
    },
    [api, selectedWorkspace, mapMembers],
  );

  const openMembers = useCallback(
    async (text, from) => {
      const params = {};
      if (from) params.from = from;
      if (selectedWorkspace) params.workspaceId = selectedWorkspace.id;
      const { data } = await api.admin.aiReplay.recentMembers(params);
      return { items: mapMembers(data.results), hasMore: data.hasMore };
    },
    [api, selectedWorkspace, mapMembers],
  );

  return (
    <>
      <Filters>
        <EntityFilter>
          <EntitySearchFilter
            placeholder="Workspaces"
            selected={selectedWorkspace}
            onSelect={(w) => {
              setSelectedWorkspace(w);
              setSelectedMember(null);
            }}
            onClear={() => setSelectedWorkspace(null)}
            searchFn={searchWorkspaces}
            onOpen={openWorkspaces}
            renderItem={renderWorkspaceItem}
            renderSelected={renderWorkspaceSelected}
          />
        </EntityFilter>
        <EntityFilter>
          <EntitySearchFilter
            placeholder="Members"
            selected={selectedMember}
            onSelect={(m) => {
              setSelectedMember(m);
              if (!selectedWorkspace && m.workspaceId) {
                setSelectedWorkspace({
                  id: m.workspaceId,
                  label: m.workspaceName,
                  key: m.workspaceKey,
                  imageUrl: m.workspaceImageUrl,
                });
              }
            }}
            onClear={() => setSelectedMember(null)}
            searchFn={searchMembers}
            onOpen={openMembers}
            renderItem={renderMemberItem}
            renderSelected={renderMemberSelected}
          />
        </EntityFilter>
        <SearchFilter>
          <TextInput
            placeholder="Search messages..."
            materialPlaceholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <ClearSearch tabIndex={-1} onClick={() => setSearchText('')}>
              <Icon icon="times" />
            </ClearSearch>
          )}
        </SearchFilter>
        <DateFilter>
          <DayPickerInput placeholder="Start" value={start} onChange={(value) => setStart(value ?? undefined)} />
        </DateFilter>
        <DateFilter>
          <DayPickerInput placeholder="End" value={end} onChange={(value) => setEnd(value ?? undefined)} />
        </DateFilter>
      </Filters>

      {isLoading && <EmptyState>Loading conversations...</EmptyState>}

      {!isLoading && conversations.length === 0 && <EmptyState>No conversations found.</EmptyState>}

      {!isLoading && conversations.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Conversation</Th>
                <Th>Workspace</Th>
                <Th>Member</Th>
                <Th>Turns</Th>
                <Th>Last Activity</Th>
              </tr>
            </thead>
            <tbody>
              {conversations.map((c) => (
                <ClickableRow
                  key={c.conversationId}
                  onClick={() => history.push(`/admin/ai/replay/${c.conversationId}`)}>
                  <Td>
                    <Preview>
                      <PreviewText>{c.title || '-'}</PreviewText>
                      {c.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
                    </Preview>
                    <Mono>{c.conversationId}</Mono>
                  </Td>
                  <Td>
                    <WorkspaceDisplay workspace={c.workspace} />
                  </Td>
                  <Td>
                    <MemberDisplay member={c.member} />
                  </Td>
                  <Td>{c.turnCount}</Td>
                  <Td title={c.lastActivity}>{moment(c.lastActivity).fromNow()}</Td>
                </ClickableRow>
              ))}
            </tbody>
          </Table>

          {hasMore && <InfiniteScrollingObserver key={conversations.length} onIntersecting={loadMore} />}
        </>
      )}
    </>
  );
}
